import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'

// Layout components
import Header from '@components/layout/Header'
import Footer from '@components/layout/Footer'
import LoadingSpinner from '@components/common/LoadingSpinner'

// Eager on purpose: lazy-loading the landing route only buys a spinner flash.
import ImprovedDashboard from '@pages/ImprovedDashboard'

const InstrumentDetail = lazy(() => import('@pages/InstrumentDetail'))
const BistStocks = lazy(() => import('@pages/BistStocks'))
const USStocks = lazy(() => import('@pages/USStocks'))
const AllStocks = lazy(() => import('@pages/AllStocks'))
const DividendStocks = lazy(() => import('@pages/DividendStocks'))
const DovizCevirici = lazy(() => import('@pages/DovizCevirici'))
const AltinHesaplama = lazy(() => import('@pages/AltinHesaplama'))
const Login = lazy(() => import('@pages/Login'))
const Register = lazy(() => import('@pages/Register'))
const StockDetail = lazy(() => import('@pages/StockDetail'))
const ETFDetail = lazy(() => import('@pages/ETFDetail'))
const CommodityDetail = lazy(() => import('@pages/CommodityDetail'))
const Portfolio = lazy(() => import('@pages/Portfolio'))
const Profile = lazy(() => import('@pages/Profile'))
const Feedback = lazy(() => import('@pages/Feedback'))
const PrivacyPolicy = lazy(() => import('@pages/PrivacyPolicy'))
const YasalUyari = lazy(() => import('@pages/YasalUyari'))
const NotFound = lazy(() => import('@pages/NotFound'))
const TurkishGold = lazy(() => import('@pages/TurkishGold'))
const Crypto = lazy(() => import('@pages/Crypto'))
const Alerts = lazy(() => import('@pages/Alerts'))
const News = lazy(() => import('@pages/News'))
const Forum = lazy(() => import('@pages/Forum'))
const EconomicCalendar = lazy(() => import('@pages/EconomicCalendar'))
const ForgotPassword = lazy(() => import('@pages/ForgotPassword'))
const ResetPassword = lazy(() => import('@pages/ResetPassword'))

// Without this the router keeps the old scroll offset, so a click from a
// scrolled list opens the next page already scrolled past its chart.
function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

// min-h keeps the footer from bouncing while a chunk loads.
function RouteFallback() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
}

function App() {
  // index.html ships static meta/OG/Twitter/canonical tags as a no-JS
  // fallback. react-helmet-async only manages tags it renders itself, so
  // it adds its own on top instead of replacing these - every route ends
  // up with two (often conflicting) copies. Remove the static ones once
  // Helmet's provider has mounted and rendered its own tags.
  useEffect(() => {
    document.querySelectorAll('[data-default="true"]').forEach((el) => el.remove())
  }, [])

  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen bg-canvas">
        <Header />
        
        <main className="flex-1">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<ImprovedDashboard />} />
              <Route path="/instrument/:symbol" element={<InstrumentDetail />} />
              <Route path="/bist" element={<BistStocks />} />
              <Route path="/us-stocks" element={<USStocks />} />
              <Route path="/all-stocks" element={<AllStocks />} />
              <Route path="/temettu-hisseleri" element={<DividendStocks />} />
              <Route path="/doviz-cevirici" element={<DovizCevirici />} />
              <Route path="/altin-hesaplama" element={<AltinHesaplama />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/stock/:symbol" element={<StockDetail />} />
              <Route path="/etf/:symbol" element={<ETFDetail />} />
              <Route path="/commodity/:symbol" element={<CommodityDetail />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/yasal-uyari" element={<YasalUyari />} />
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/altin" element={<TurkishGold />} />
              <Route path="/turkish-gold" element={<Navigate to="/altin" replace />} />
              <Route path="/crypto" element={<Crypto />} />
              <Route path="/alerts" element={<Alerts />} />
              <Route path="/news" element={<News />} />
              <Route path="/forum" element={<Forum />} />
              <Route path="/ekonomik-takvim" element={<EconomicCalendar />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        
        <Footer />
      </div>
    </Router>
  )
}

export default App
